import React, { useEffect, useState } from 'react';
import { useApi } from '@/lib/useApi';
import ContractorDashboard from '@/components/ContractorDashboard';
// CB:BEGIN priority2-component-imports
import { ContractorProfileEditor } from '@/components/admin/ContractorProfileEditor';
import { PerformanceControls } from '@/components/admin/PerformanceControls';
// CB:END priority2-component-imports

interface AdminContractorDetailProps {
  contractorId: string;
  onNavigate: (screen: string) => void;
}

export default function AdminContractorDetail({ contractorId, onNavigate }: AdminContractorDetailProps) {
  const api = useApi();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Editable fields
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [businessName, setBusinessName] = useState('');
  const [businessAddress, setBusinessAddress] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');
  const [verified, setVerified] = useState<boolean>(false);

  const load = async () => {
    try {
      setLoading(true);
      const d = await api.get(`/api/admin/contractors/${contractorId}`);
      setData(d);
      setEmail(d?.user?.email || '');
      setPhone(d?.user?.phone || '');
      setBusinessName(d?.businessName || '');
      setBusinessAddress(d?.businessAddress || '');
      setLicenseNumber(d?.licenseNumber || '');
      setVerified(!!d?.verified);
    } catch (e: any) {
      setError(e?.message || 'Failed to load contractor');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [contractorId]);

  const save = async () => {
    try {
      await api.put(`/api/admin/contractors/${contractorId}`, {
        email, phone, businessName, businessAddress, licenseNumber, verified
      } as any);
      await load();
    } catch (e: any) {
      setError(e?.message || 'Failed to save changes');
    }
  };

  if (loading) return <div className="p-8">Loading...</div>;
  if (error) return <div className="p-8 text-red-600">{error}</div>;
  if (!data) return <div className="p-8">Not found</div>;

  return (
    <div className="max-w-7xl mx-auto p-6">
      <button onClick={() => onNavigate('admin-contractors-list')} className="text-blue-600 hover:text-blue-800 mb-4">← Back to Contractors</button>
      <h1 className="text-3xl font-bold text-gray-900 mb-6">{data.businessName || 'Contractor'}</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: View-as dashboard style cards */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Contractor Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <div className="text-gray-500">Email</div>
                <div className="text-gray-900">{data?.user?.email || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Phone</div>
                <div className="text-gray-900">{data?.user?.phone || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">License #</div>
                <div className="text-gray-900">{data?.licenseNumber || '—'}</div>
              </div>
              <div>
                <div className="text-gray-500">Verified</div>
                <div className="text-gray-900">{data?.verified ? 'Yes' : 'No'}</div>
              </div>
            </div>
          </div>

          {/* CB:BEGIN contractor-review-summary */}
          {(data?.ratingAverage || data?.reviewCount > 0) && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <h3 className="text-lg font-semibold mb-4">Review Summary</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                <div>
                  <div className="text-gray-500">Overall Rating</div>
                  <div className="text-gray-900 font-semibold">
                    {data.ratingAverage ? `${Number(data.ratingAverage).toFixed(1)} / 5` : '—'}
                    <span style={{ color: '#f59e0b', marginLeft: 4 }}>★</span>
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Reviews</div>
                  <div className="text-gray-900 font-semibold">{data.reviewCount ?? 0}</div>
                </div>
                <div>
                  <div className="text-gray-500">Recommend Rate</div>
                  <div className="text-gray-900 font-semibold">
                    {data.recommendationRate != null ? `${Math.round(data.recommendationRate)}%` : '—'}
                  </div>
                </div>
                <div>
                  <div className="text-gray-500">Workmanship</div>
                  <div className="text-gray-900 font-semibold">
                    {data.averageWorkmanshipRating ? Number(data.averageWorkmanshipRating).toFixed(1) : '—'}
                  </div>
                </div>
              </div>
              <div style={{ marginTop: 10 }}>
                <button
                  className="text-blue-600 hover:text-blue-800 text-sm"
                  onClick={() => onNavigate('admin-reviews')}
                >
                  View all reviews →
                </button>
              </div>
            </div>
          )}
          {/* CB:END contractor-review-summary */}

          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Recent Bids</h3>
            {Array.isArray(data?.bids) && data.bids.length > 0 ? (
              <div className="space-y-3">
                {data.bids.map((b: any) => (
                  <div key={b.id} className="border border-gray-200 rounded-lg p-4 flex items-center justify-between">
                    <div>
                      <div className="font-medium">{(b.amountCents/100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}</div>
                      <div className="text-sm text-gray-600">{new Date(b.createdAt).toLocaleDateString()}</div>
                    </div>
                    <span className="px-2 py-1 rounded text-xs bg-gray-100 text-gray-800">{b.status}</span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-sm text-gray-600">No bids yet</div>
            )}
          </div>

          {/* View-as Contractor Dashboard (headerless) */}
          <div className="bg-gray-50 rounded-lg p-4">
            <ContractorDashboard onNavigate={onNavigate} adminView={true} />
          </div>
        </div>

        {/* Right: Editable fields */}
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <h3 className="text-lg font-semibold mb-4">Edit Contractor</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input value={email} onChange={(e) => setEmail(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input value={phone} onChange={(e) => setPhone(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Name</label>
                <input value={businessName} onChange={(e) => setBusinessName(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Business Address</label>
                <input value={businessAddress} onChange={(e) => setBusinessAddress(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">License Number</label>
                <input value={licenseNumber} onChange={(e) => setLicenseNumber(e.target.value)} className="w-full px-3 py-2 border rounded-md" />
              </div>
              <label className="inline-flex items-center gap-2 text-sm">
                <input type="checkbox" checked={verified} onChange={(e) => setVerified(e.target.checked)} />
                Verified
              </label>
              <div className="flex gap-3 pt-2">
                <button onClick={save} className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">Save Changes</button>
                <button onClick={load} className="px-4 py-2 bg-gray-100 rounded-md">Reset</button>
              </div>
            </div>
          </div>

          {/* CB:BEGIN priority2-components */}
          {/* Contractor Profile Editor */}
          <ContractorProfileEditor
            contractorId={contractorId}
            contractor={{ company: data?.company || data?.businessName }}
            verification={data?.contractor_verification}
            onSuccess={load}
          />

          {/* Performance Controls */}
          <PerformanceControls
            contractorId={contractorId}
            onSuccess={load}
          />
          {/* CB:END priority2-components */}
        </div>
      </div>
    </div>
  );
}


